// CELL-BIO9 — WebAudio engine.
// Hum loop, typewriter key clicks, choice beeps, sting cues.
// All audio is opt-in (starts on first user gesture). Respects prefers-reduced-motion
// for visual sync but audio is always available unless the user mutes.

const HUM_FREQ = 60;       // Hz — power-line hum
const HUM_GAIN = 0.04;
const CLICK_GAIN = 0.18;
const BEEP_GAIN = 0.22;
const STING_GAIN = 0.35;

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private humGain: GainNode | null = null;
  private muted = false;
  private started = false;

  /** Call once on first user gesture to unlock AudioContext. */
  start(): void {
    if (this.started) return;
    this.started = true;
    try {
      this.ctx = new AudioContext();
      this.startHum();
    } catch {
      // AudioContext unavailable — degrade silently.
      this.ctx = null;
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.humGain) {
      this.humGain.gain.setTargetAtTime(muted ? 0 : HUM_GAIN, this.ctx!.currentTime, 0.1);
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Short typewriter key click (played per character during typewriter animation). */
  click(): void {
    if (!this.ctx || this.muted) return;
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.012, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      // White noise burst, decaying fast.
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 8);
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const gain = this.ctx.createGain();
    gain.gain.value = CLICK_GAIN;
    src.connect(gain);
    gain.connect(this.ctx.destination);
    src.start();
  }

  /** Short beep for choice selection. */
  choiceBeep(index: number): void {
    if (!this.ctx || this.muted) return;
    // Slightly different pitch per choice index.
    const freq = 440 + index * 55;
    this.tone(freq, 0.06, BEEP_GAIN, "square");
  }

  /** Sting: played on node transitions with day_advance. */
  dayStingAdvance(): void {
    if (!this.ctx || this.muted) return;
    // Rising two-note sting.
    this.tone(330, 0.08, STING_GAIN, "sine", 0);
    this.tone(440, 0.12, STING_GAIN, "sine", 0.09);
  }

  /** Sting: played when Heat crosses a threshold. */
  heatStingWarning(): void {
    if (!this.ctx || this.muted) return;
    // Descending dissonant sting.
    this.tone(880, 0.1, STING_GAIN, "sawtooth", 0);
    this.tone(831, 0.15, STING_GAIN * 0.6, "sawtooth", 0.05);
  }

  /** Sting: played at chapter cliffhanger / end of slice. */
  endStingCliffhanger(): void {
    if (!this.ctx || this.muted) return;
    this.tone(220, 0.4, STING_GAIN, "sine", 0);
    this.tone(165, 0.5, STING_GAIN * 0.7, "sine", 0.15);
    this.tone(110, 0.6, STING_GAIN * 0.4, "sine", 0.3);
  }

  private startHum(): void {
    if (!this.ctx) return;
    const humNode = this.ctx.createOscillator();
    this.humGain = this.ctx.createGain();
    humNode.type = "sine";
    humNode.frequency.value = HUM_FREQ;
    this.humGain.gain.value = this.muted ? 0 : HUM_GAIN;
    humNode.connect(this.humGain);
    this.humGain.connect(this.ctx.destination);
    humNode.start();
  }

  private tone(
    freq: number,
    duration: number,
    gain: number,
    type: OscillatorType,
    delaySeconds = 0
  ): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0, this.ctx.currentTime + delaySeconds);
    g.gain.linearRampToValueAtTime(gain, this.ctx.currentTime + delaySeconds + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + delaySeconds + duration);
    osc.connect(g);
    g.connect(this.ctx.destination);
    osc.start(this.ctx.currentTime + delaySeconds);
    osc.stop(this.ctx.currentTime + delaySeconds + duration + 0.01);
  }
}

export const audioEngine = new AudioEngine();
